import { can, canAccessWorkspace, isClientAdmin, isPlatformAdmin } from "./accessControl";

export const isAgentWorkspacePath = (pathname) => (
  /^\/agents\/[^/]+\/(customers|prescriptions|inbox|leads|edit|agent-config|bot-config|meta-config|forms|appointments|providers|services|booking-settings|notifications|schedulers)(?:\/|$)/.test(pathname)
);

export function agentConfigTabAccess(user, clientId = user?.client_id) {
  const allows = (...permissions) => permissions.some((permission) => can(user, permission, clientId));
  const configuration = allows("bot_configuration.read");
  return {
    basic: configuration,
    details: configuration,
    "chat-settings": configuration,
    tools: isClientAdmin(user, clientId),
    flow: configuration,
    providers: allows("providers.read", "providers.update", "providers.manage", "provider_hours.read",
      "provider_hours.manage", "appointment_configuration.read", "appointment_configuration.update"),
    services: allows("services.read", "services.manage", "appointment_configuration.read", "appointment_configuration.update"),
    appointments: allows("appointment_configuration.read"),
    schedulers: allows("schedules.read"),
    forms: configuration || allows("form_templates.read", "notification_configuration.read",
      "consent_requests.read", "consent_requests.create"),
    "whatsapp-templates": allows("whatsapp_templates.read"),
  };
}

export function canAccessAgentConfig(user, clientId = user?.client_id) {
  return Object.values(agentConfigTabAccess(user, clientId)).some(Boolean);
}

export function defaultBotSection(user, clientId = user?.client_id) {
  const sections = ["customers", "prescriptions", "inbox", "leads", "providers", "services", "booking-settings", "schedulers", "notifications", "agent-config"];
  const section = sections.find((entry) => permissionForBotSection(entry, user, clientId));
  return ["providers", "services"].includes(section) && canAccessAgentConfig(user, clientId) ? "agent-config" : section || null;
}

const permissionForBotSection = (section, user, clientId) => {
  const allows = (...permissions) => permissions.some((permission) => can(user, permission, clientId));
  if (section === "customers") return allows("customers.read");
  if (section === "prescriptions") return allows("prescriptions.read", "prescriptions.create", "prescription_templates.manage");
  if (section === "inbox") return allows("inbox.read");
  if (section === "leads") return allows(
    "leads.read", "lead_discovery.read", "lead_discovery.configure", "lead_outreach.read",
  );
  if (section === "providers") return allows(
    "providers.read", "providers.update", "providers.manage", "provider_hours.read", "provider_hours.manage",
    "booking_lookups.read", "appointment_configuration.read",
  );
  if (section === "services") return allows(
    "services.read", "services.manage", "booking_lookups.read", "appointment_configuration.read",
  );
  if (section === "booking-settings") return allows("appointment_configuration.read");
  if (section === "schedulers") return allows("schedules.read");
  if (section === "notifications") return allows("notification_configuration.read");
  if (section === "agent-config" || section === "bot-config") return canAccessAgentConfig(user, clientId);
  if (section === "edit") return allows("bot_configuration.update");
  return false;
};

export function routeAllowed(user, pathname, search = "") {
  if (!user) return false;
  if (pathname === "/account/security") return true;
  if (user.must_change_password) return false;
  if (isPlatformAdmin(user)) return true;
  if (pathname === "/clients" || pathname === "/clients/new-client") return false;
  if (pathname.startsWith("/clients/")) {
    const match = pathname.match(/^\/clients\/([^/]+)(?:\/|$)/);
    const clientId = match?.[1];
    if (!clientId) return false;
    if (/^\/clients\/[^/]+\/(users|roles)(?:\/|$)/.test(pathname)) {
      return isClientAdmin(user, clientId);
    }
    return isClientAdmin(user, clientId);
  }
  if (pathname === "/agents" || pathname === "/bot-home") return canAccessWorkspace(user);
  if (pathname === "/customers") return can(user, "customers.read");
  if (pathname === "/chat" || pathname.startsWith("/whatsapp-inbox")) {
    return can(user, "inbox.read");
  }
  if (pathname.startsWith("/agents/")) {
    const parts = pathname.split("/").filter(Boolean);
    const section = parts[2];
    if (!section) return canAccessWorkspace(user);
    if (section === "appointments") return can(user, "appointments.read") || can(user, "appointment_configuration.read");
    if (section === "forms") return ["form_templates.read", "form_templates.create", "consent_requests.read",
      "consent_requests.create", "bot_configuration.read", "notification_configuration.read"]
      .some((permission) => can(user, permission));
    if (section === "prescriptions") return ["prescriptions.read", "prescriptions.create", "prescription_templates.manage"].some((permission) => can(user, permission));
    if (section === "leads") {
      if (parts[3]) return can(user, "leads.read");
      const tab = new URLSearchParams(search).get("tab");
      if (tab === "discover") return can(user, "lead_discovery.read");
      if (tab === "integrations") return can(user, "lead_discovery.configure");
      if (tab === "outreach") return can(user, "lead_outreach.read");
      if (tab === "database") return can(user, "leads.read");
      return permissionForBotSection(section, user);
    }
    if (section === "agent-config" || section === "bot-config") {
      const params = new URLSearchParams(search);
      const requested = params.get("tab");
      if (!requested) return canAccessAgentConfig(user);
      const tab = requested === "whatsapp" ? "whatsapp-templates" : requested;
      const access = agentConfigTabAccess(user);
      if (access[tab] !== true) return false;
      if (tab === "appointments" && params.has("section")) {
        const schedulingAccess = {
          appointments: can(user, "appointments.read") || can(user, "appointment_configuration.read"),
          providers: access.providers,
          services: access.services,
          settings: can(user, "appointment_configuration.read"),
          notifications: can(user, "notification_configuration.read"),
        };
        return schedulingAccess[params.get("section")] === true;
      }
      return true;
    }
    return permissionForBotSection(section, user);
  }
  if (pathname === "/dashboard") return isClientAdmin(user);
  if (pathname.startsWith("/admin/") || pathname.startsWith("/tenants")
    || pathname.startsWith("/services") || pathname.startsWith("/onboarding")) return false;
  return false;
}
